import React, { useState, useCallback } from 'react';

/**
 * Shared confirmation modal — a drop-in replacement for window.confirm().
 *
 * Usage:
 *   const { confirm, confirmModal } = useConfirm();
 *   ...
 *   if (!(await confirm({ title: 'Delete Project', message: 'Are you sure?', danger: true }))) return;
 *   ...
 *   return (<div> ... {confirmModal} </div>);
 *
 * `confirm()` returns a Promise<boolean> that resolves true (confirmed) or
 * false (cancelled / dismissed). Accepts either an options object or a plain
 * string (used as the message).
 *
 * Options: { title, message, confirmLabel, cancelLabel, danger }
 */
export function useConfirm() {
    const [state, setState] = useState(null);

    const confirm = useCallback((opts) => new Promise((resolve) => {
        const options = typeof opts === 'string' ? { message: opts } : (opts || {});
        setState({ ...options, resolve });
    }), []);

    const close = (result) => {
        if (state?.resolve) state.resolve(result);
        setState(null);
    };

    const confirmModal = <ConfirmModal state={state} onClose={close} themeColor={state?.themeColor ?? true} />;

    return { confirm, confirmModal };
}

/**
 * Presentational modal. Renders nothing when `state` is null.
 *
 * @param {boolean} themeColor  true (default) → dark theme matching CommonModal
 *                              (#0f172a bg, #1e293b border); false → white theme.
 */
export function ConfirmModal({ state, onClose, themeColor = true }) {
    if (!state) return null;
    const { title, message, confirmLabel, cancelLabel, danger } = state;
    const themeClass = themeColor ? 'cmn-confirm-box--dark' : 'cmn-confirm-box--light';

    return (
        <div className="cmn-confirm-overlay" onClick={() => onClose(false)}>
            <style>{CONFIRM_MODAL_CSS}</style>
            <div className={`cmn-confirm-box ${themeClass}`} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                <div className="cmn-confirm-title">{title || 'Please confirm'}</div>
                <div className="cmn-confirm-message">{message}</div>
                <div className="cmn-confirm-actions">
                    <button type="button" className="cmn-confirm-btn cmn-confirm-btn--cancel" onClick={() => onClose(false)}>
                        {cancelLabel || 'Cancel'}
                    </button>
                    <button
                        type="button"
                        className={`cmn-confirm-btn ${danger ? 'cmn-confirm-btn--danger' : 'cmn-confirm-btn--primary'}`}
                        onClick={() => onClose(true)}
                        autoFocus
                    >
                        {confirmLabel || 'Confirm'}
                    </button>
                </div>
            </div>
        </div>
    );
}

const CONFIRM_MODAL_CSS = `
.cmn-confirm-overlay {
    position: fixed; inset: 0; z-index: 10050;
    background: rgba(9, 30, 66, 0.54);
    display: flex; align-items: center; justify-content: center;
    animation: cmn-confirm-fade 0.16s ease-out;
}
.cmn-confirm-box {
    width: 440px; max-width: calc(100vw - 32px);
    padding: 24px 26px;
    animation: cmn-confirm-pop 0.18s cubic-bezier(0.16, 1, 0.3, 1);
    font-family: 'Outfit', sans-serif;
}
.cmn-confirm-title { font-size: 16px; font-weight: 600; margin-bottom: 8px; }
.cmn-confirm-message { font-size: 14px; line-height: 1.55; margin-bottom: 22px; }
.cmn-confirm-actions { display: flex; gap: 8px; justify-content: flex-end; }
.cmn-confirm-btn {
    padding: 8px 18px; font-size: 13px; font-weight: 600; border-radius: 6px;
    cursor: pointer; font-family: 'Outfit', sans-serif; border: 1px solid transparent; transition: background 0.15s ease;
}

/* ── Light theme (themeColor={false}) ── */
.cmn-confirm-box--light {
    background: #ffffff; border-radius: 10px;
    box-shadow: 0 16px 40px -8px rgba(9, 30, 66, 0.35), 0 0 1px rgba(9, 30, 66, 0.30);
}
.cmn-confirm-box--light .cmn-confirm-title { color: #172b4d; }
.cmn-confirm-box--light .cmn-confirm-message { color: #5e6c84; }
.cmn-confirm-box--light .cmn-confirm-btn--cancel { background: transparent; border-color: #dfe1e6; color: #5e6c84; }
.cmn-confirm-box--light .cmn-confirm-btn--cancel:hover { background: #f4f5f7; }
.cmn-confirm-box--light .cmn-confirm-btn--primary { background: #0052cc; color: #fff; }
.cmn-confirm-box--light .cmn-confirm-btn--primary:hover { background: #0747a6; }
.cmn-confirm-box--light .cmn-confirm-btn--danger { background: #de350b; color: #fff; }
.cmn-confirm-box--light .cmn-confirm-btn--danger:hover { background: #bf2600; }

/* ── Dark theme (themeColor={true}, default — matches CommonModal) ── */
.cmn-confirm-box--dark {
    background: #0f172a; border: 1px solid #1e293b; border-radius: 14px;
    box-shadow: 0 24px 60px -12px rgba(0, 0, 0, 0.6);
}
.cmn-confirm-box--dark .cmn-confirm-title { color: #f1f5f9; }
.cmn-confirm-box--dark .cmn-confirm-message { color: #94a3b8; }
.cmn-confirm-box--dark .cmn-confirm-btn--cancel { background: transparent; border-color: #334155; color: #cbd5e1; }
.cmn-confirm-box--dark .cmn-confirm-btn--cancel:hover { background: #1e293b; }
.cmn-confirm-box--dark .cmn-confirm-btn--primary { background: #3b82f6; color: #fff; }
.cmn-confirm-box--dark .cmn-confirm-btn--primary:hover { background: #2563eb; }
.cmn-confirm-box--dark .cmn-confirm-btn--danger { background: #dc2626; color: #fff; }
.cmn-confirm-box--dark .cmn-confirm-btn--danger:hover { background: #b91c1c; }
@keyframes cmn-confirm-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes cmn-confirm-pop { from { opacity: 0; transform: translateY(8px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
`;

export default ConfirmModal;
